import { validateIdentityBackup } from "@/lib/bap/identity-backup";
/**
 * PRF Hook (merged register + unlock)
 *
 * Provides functionality for:
 * - Registering a new PRF credential for backup encryption
 * - Unlocking backups with WebAuthn PRF (biometric)
 *
 * PRF salt is stored in localStorage (client-side) rather than fetched from server.
 */

import { useCallback, useEffect, useState } from "react";
import { logger } from "@/lib/logger";
import { STORAGE_KEYS } from "@/lib/storage-keys";
import {
	base64ToArrayBuffer,
	decryptWithPRF,
	encryptWithPRF,
} from "@/lib/webauthn/encryption";
import {
	authenticateWithPRF,
	checkPRFSupport,
	registerPRFCredential,
	toClientCredential,
} from "@/lib/webauthn/prf";
import type { PRFEncryptedBackup } from "@/lib/webauthn/types";

interface PRFRegistrationResultWithInternal {
	success: boolean;
	credentialId?: string;
	prfSalt?: string;
	error?: string;
	_internal?: {
		publicKey: string;
		transports: string[];
		prfSalt: string;
	};
}

interface UsePRFResult {
	/** Whether PRF is supported by this browser */
	isSupported: boolean;
	/** Whether user has a PRF credential registered for this device */
	hasPRFCredential: boolean;
	/** Whether a PRF operation is currently in progress */
	isWorking: boolean;
	/** Error message if an operation failed */
	error: string | null;
	/** Register a PRF credential and encrypt the backup */
	register: (
		backup: unknown,
		deviceName?: string
	) => Promise<{ success: boolean; credentialId?: string }>;
	/** Unlock (decrypt) the backup using PRF */
	unlock: () => Promise<string | null>;
	/** Clear any error state */
	clearError: () => void;
}

/**
 * Fetch registration options from the server.
 */
async function fetchRegistrationOptions(): Promise<{
	challengeId: string;
	options: Record<string, unknown>;
	rpId: string;
}> {
	const res = await fetch("/api/webauthn/prf/register", {
		credentials: "include",
	});

	if (!res.ok) {
		const errorData = await res.json();
		throw new Error(errorData.message || "Failed to get registration options");
	}

	const { challengeId, options, rpId } = await res.json();
	return { challengeId, options, rpId };
}

/**
 * Complete credential registration with the server.
 */
async function completeRegistration(
	challengeId: string,
	credentialId: string,
	internalData: { publicKey: string; transports: string[] },
	prfSalt: string,
	deviceName: string | undefined
): Promise<void> {
	const res = await fetch("/api/webauthn/prf/register", {
		method: "POST",
		headers: { "Content-Type": "application/json" },
		credentials: "include",
		body: JSON.stringify({
			challengeId,
			credential: {
				credentialId,
				publicKey: internalData.publicKey,
				transports: internalData.transports,
			},
			prfSalt,
			deviceName:
				deviceName ||
				`${navigator.platform || "Device"} - ${new Date().toLocaleDateString()}`,
		}),
	});

	if (!res.ok) {
		const errorData = await res.json();
		throw new Error(errorData.message || "Failed to store credential");
	}
}

/**
 * Authenticate with PRF using salt from localStorage, then encrypt the backup.
 */
async function authenticateAndEncrypt(
	credentialId: string,
	prfSalt: string,
	backup: unknown
): Promise<void> {
	validateIdentityBackup(backup);
	const authOptionsRes = await fetch(
		`/api/webauthn/prf/authenticate?credentialId=${encodeURIComponent(credentialId)}`
	);
	if (!authOptionsRes.ok) {
		throw new Error("Failed to get authentication options");
	}

	const { options: authOptions, rpId } = await authOptionsRes.json();

	const clientCredential = toClientCredential({
		credentialId,
		prfSalt,
	});

	const authResult = await authenticateWithPRF({
		credential: clientCredential,
		challenge: base64ToArrayBuffer(authOptions.challenge),
		rpId,
	});

	if (!(authResult.success && authResult.encryptionKey)) {
		throw new Error(authResult.error || "PRF authentication failed");
	}

	const encryptedBackup = await encryptWithPRF(
		backup,
		authResult.encryptionKey,
		credentialId
	);

	const encryptedBackupStr = JSON.stringify(encryptedBackup);
	localStorage.setItem(STORAGE_KEYS.PRF_ENCRYPTED_BACKUP, encryptedBackupStr);
	localStorage.setItem(STORAGE_KEYS.PRF_CREDENTIAL_ID, credentialId);
	localStorage.setItem(STORAGE_KEYS.PRF_SALT, prfSalt);

	// Persist the ciphertext server-side so biometric unlock can be restored
	// after a localStorage wipe / on another device. Non-fatal for registration
	// (it already succeeded locally), but a failure here means cross-device /
	// post-wipe restore silently won't work — so surface it, never mask it.
	try {
		const res = await fetch("/api/webauthn/prf/credential", {
			method: "PUT",
			headers: { "Content-Type": "application/json" },
			credentials: "include",
			body: JSON.stringify({ encryptedBackup: encryptedBackupStr }),
		});
		if (!res.ok) {
			logger.warn(
				{ status: res.status },
				"PRF backup server-side persistence failed (non-2xx); cross-device/post-wipe biometric restore will be unavailable until the next successful save"
			);
		}
	} catch (err) {
		logger.warn(
			{ err },
			"PRF backup server-side persistence failed (network); cross-device/post-wipe biometric restore will be unavailable until the next successful save"
		);
	}
}

/**
 * Load the encrypted backup from localStorage.
 */
function loadEncryptedBackup(): PRFEncryptedBackup {
	const encryptedBackupStr = localStorage.getItem(
		STORAGE_KEYS.PRF_ENCRYPTED_BACKUP
	);
	if (!encryptedBackupStr) {
		throw new Error("No PRF-encrypted backup found");
	}
	return JSON.parse(encryptedBackupStr);
}

/**
 * Perform PRF unlock: authenticate with PRF using salt from localStorage,
 * then decrypt the backup.
 */
async function performPRFUnlock(
	credentialId: string,
	prfSalt: string
): Promise<string> {
	const encryptedBackup = loadEncryptedBackup();

	const authOptionsRes = await fetch(
		`/api/webauthn/prf/authenticate?credentialId=${encodeURIComponent(credentialId)}`
	);
	if (!authOptionsRes.ok) {
		throw new Error("Failed to get authentication options");
	}

	const { challengeId, options, rpId } = await authOptionsRes.json();

	const clientCredential = toClientCredential({
		credentialId,
		prfSalt,
	});

	const prfResult = await authenticateWithPRF({
		credential: clientCredential,
		challenge: base64ToArrayBuffer(options.challenge),
		rpId,
	});

	if (!(prfResult.success && prfResult.encryptionKey)) {
		throw new Error(prfResult.error || "PRF authentication failed");
	}

	const decryptedBackupStr = await decryptWithPRF(
		encryptedBackup,
		prfResult.encryptionKey
	);

	validateIdentityBackup(JSON.parse(decryptedBackupStr));
	if (
		JSON.stringify(loadEncryptedBackup()) !== JSON.stringify(encryptedBackup)
	) {
		throw new Error("Backup changed during passkey unlock");
	}

	// Verify with server (update counter) - fire and forget
	fetch("/api/webauthn/prf/authenticate", {
		method: "POST",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify({
			challengeId,
			credentialId: prfResult.credentialId,
		}),
	}).catch((err) => {
		logger.warn({ error: err }, "PRF auth verification failed");
	});

	return decryptedBackupStr;
}

export function usePRF(): UsePRFResult {
	const [isSupported, setIsSupported] = useState(false);
	const [isWorking, setIsWorking] = useState(false);
	const [error, setError] = useState<string | null>(null);

	// Sync-initialize credential ID and salt from localStorage
	const [credentialId, setCredentialId] = useState<string | null>(() => {
		if (typeof window === "undefined") {
			return null;
		}
		return localStorage.getItem(STORAGE_KEYS.PRF_CREDENTIAL_ID);
	});

	const [prfSalt, setPrfSalt] = useState<string | null>(() => {
		if (typeof window === "undefined") {
			return null;
		}
		return localStorage.getItem(STORAGE_KEYS.PRF_SALT);
	});

	// Re-read after the unlock gate hydrates PRF data from the server into
	// localStorage (e.g. post-wipe). Without this, hasPRFCredential is fixed at
	// mount and the auto-unlock would never see a server-restored credential.
	useEffect(() => {
		const onHydrated = () => {
			setCredentialId(localStorage.getItem(STORAGE_KEYS.PRF_CREDENTIAL_ID));
			setPrfSalt(localStorage.getItem(STORAGE_KEYS.PRF_SALT));
		};
		window.addEventListener("prf-credential-hydrated", onHydrated);
		return () =>
			window.removeEventListener("prf-credential-hydrated", onHydrated);
	}, []);

	// Check PRF browser support on mount
	useEffect(() => {
		const doCheck = async () => {
			const supported = await checkPRFSupport();
			setIsSupported(supported);
		};
		doCheck();
	}, []);

	const hasPRFCredential = Boolean(credentialId && prfSalt);

	const register = useCallback(
		async (
			backup: unknown,
			deviceName?: string
		): Promise<{ success: boolean; credentialId?: string }> => {
			setIsWorking(true);
			setError(null);

			try {
				// Step 1: Get registration options
				const regData = await fetchRegistrationOptions();

				// Step 2: Create credential with PRF extension via browser
				const regResult: PRFRegistrationResultWithInternal =
					await registerPRFCredential({
						challenge: base64ToArrayBuffer(
							(regData.options as Record<string, string>).challenge
						),
						rpId: regData.rpId,
						rpName: (regData.options as Record<string, Record<string, string>>)
							.rp.name,
						userId: base64ToArrayBuffer(
							(regData.options as Record<string, Record<string, string>>).user
								.id
						),
						userName: (
							regData.options as Record<string, Record<string, string>>
						).user.name,
						userDisplayName: (
							regData.options as Record<string, Record<string, string>>
						).user.displayName,
					});

				if (!(regResult.success && regResult.credentialId)) {
					throw new Error(regResult.error || "Credential registration failed");
				}

				if (!regResult._internal) {
					throw new Error("Missing credential data");
				}

				// The salt is generated client-side by registerPRFCredential
				const salt = regResult.prfSalt || regResult._internal.prfSalt;
				if (!salt) {
					throw new Error("Missing PRF salt from registration");
				}

				// Step 3: Complete registration with server (salt sent as backup reference)
				await completeRegistration(
					regData.challengeId,
					regResult.credentialId,
					regResult._internal,
					salt,
					deviceName
				);

				// Step 4: Authenticate and encrypt backup using client-side salt
				await authenticateAndEncrypt(regResult.credentialId, salt, backup);

				logger.info(
					{ credentialId: regResult.credentialId },
					"PRF credential registered and backup encrypted"
				);

				return { success: true, credentialId: regResult.credentialId };
			} catch (err) {
				const message =
					err instanceof Error ? err.message : "PRF registration failed";
				logger.error({ error: err }, "PRF registration failed");
				setError(message);
				return { success: false };
			} finally {
				setIsWorking(false);
			}
		},
		[]
	);

	const unlock = useCallback(async (): Promise<string | null> => {
		if (!(isSupported && credentialId && prfSalt)) {
			setError("PRF not available");
			return null;
		}

		setIsWorking(true);
		setError(null);

		try {
			const result = await performPRFUnlock(credentialId, prfSalt);
			logger.info("PRF unlock successful");
			return result;
		} catch (err) {
			const message = err instanceof Error ? err.message : "PRF unlock failed";
			logger.error({ error: err }, "PRF unlock failed");
			setError(message);
			return null;
		} finally {
			setIsWorking(false);
		}
	}, [isSupported, credentialId, prfSalt]);

	const clearError = useCallback(() => {
		setError(null);
	}, []);

	return {
		isSupported,
		hasPRFCredential,
		isWorking,
		error,
		register,
		unlock,
		clearError,
	};
}
